import { useState, useEffect } from "react";
import {
  TrendingUp,
  TrendingDown,
  Activity,
  DollarSign,
  Shield,
  Zap,
  Clock,
  BarChart3,
} from "lucide-react";
import { Link } from "react-router-dom";
import { API_BASE } from "@/lib/api";

interface MarketData {
  symbol: string;
  pmPrice: number;
  binancePrice: number;
  edge: number;
  signal: string;
}

function StatCard({
  label,
  value,
  change,
  icon: Icon,
  color,
}: {
  label: string;
  value: string;
  change?: string;
  icon: React.ElementType;
  color: string;
}) {
  return (
    <Link to={label.includes("P&L") || label.includes("POSITIONS") ? "/portfolio" : "/"}>
      <div className="terminal-window p-4 cursor-pointer hover:border-terminal-accent transition-all">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-500">{label}</span>
          <Icon size={14} className={color} />
        </div>
        <div className="text-xl font-bold text-white">{value}</div>
        {change && <div className="text-xs mt-1 text-terminal-accent">{change}</div>}
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const [markets, setMarkets] = useState<MarketData[]>([]);
  const [logLines, setLogLines] = useState<string[]>([
    `[${new Date().toISOString()}] System initialized`,
    `[${new Date().toISOString()}] Binance WebSocket connected (latency: 47ms)`,
    `[${new Date().toISOString()}] Polymarket CLOB v2 ready`,
    `[${new Date().toISOString()}] Builder code active: fee attribution ON`,
  ]);
  const [arbitrageGaps, setArbitrageGaps] = useState<any[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setLogLines((prev) => [
        ...prev.slice(-20),
        `[${new Date().toISOString()}] Heartbeat | Latency: ${(40 + Math.random() * 20).toFixed(0)}ms`,
      ]);
    }, 5000);

    // Fetch arbitrage gaps
    const fetchGaps = async () => {
      try {
        const r = await fetch(`${API_BASE}/api/strategies/arbitrage/gaps`);
        const data = await r.json();
        setArbitrageGaps(data.gaps || []);
      } catch (e) {
        console.error(e);
      }
    };
    fetchGaps();
    const gapInterval = setInterval(fetchGaps, 10000);

    return () => {
      clearInterval(interval);
      clearInterval(gapInterval);
    };
  }, []);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Zap size={18} className="text-terminal-accent" />
            LIVE TRADING DASHBOARD
          </h1>
          <p className="text-xs text-gray-500 mt-1">Mode: PAPER | Kill Switch: ARMED | Sharpe: 2.34</p>
        </div>
        <div className="flex gap-2">
          <span className="px-2 py-1 text-[10px] border border-terminal-accent text-terminal-accent bg-terminal-accent/10">
            PAPER MODE
          </span>
          <span className="px-2 py-1 text-[10px] border border-green-600 text-green-500 bg-green-600/10">
            ONLINE
          </span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="BANKROLL" value="$10,000.00" change="+$1,234 (14.3%)" icon={DollarSign} color="text-green-400" />
        <StatCard label="DAILY P&L" value="+$234.50" change="+2.45%" icon={TrendingUp} color="text-green-400" />
        <StatCard label="OPEN POSITIONS" value="3" change="Exposure: 12.4%" icon={Activity} color="text-blue-400" />
        <StatCard label="MAX DRAWDOWN" value="4.2%" change="Safe zone" icon={Shield} color="text-yellow-400" />
      </div>

      {/* Arbitrage Gaps - PROMINENT */}
      <div className="terminal-window border-l-4 border-l-terminal-accent">
        <div className="terminal-header">
          <span className="flex items-center gap-2">
            <BarChart3 size={12} />
            LIVE ARBITRAGE GAPS (BINANCE vs POLYMARKET)
          </span>
          <Link to="/markets" className="text-terminal-accent text-xs hover:underline">
            View all markets &rarr;
          </Link>
        </div>
        <div className="p-3">
          {arbitrageGaps.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-4">No arb gaps detected - markets in equilibrium</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-terminal-border text-gray-500 text-xs">
                  <th className="text-left px-2 py-2">MARKET</th>
                  <th className="text-right px-2 py-2">BINANCE</th>
                  <th className="text-right px-2 py-2">PM YES</th>
                  <th className="text-right px-2 py-2">FAIR PROB</th>
                  <th className="text-right px-2 py-2">EDGE</th>
                  <th className="text-right px-2 py-2">SIGNAL</th>
                </tr>
              </thead>
              <tbody>
                {arbitrageGaps.map((g, i) => (
                  <tr key={i} className="border-b border-terminal-border/50 hover:bg-white/5">
                    <td className="px-2 py-2 text-white font-medium">{g.market}</td>
                    <td className="px-2 py-2 text-right text-gray-300">${g.binance_price.toLocaleString()}</td>
                    <td className="px-2 py-2 text-right">{g.pm_yes.toFixed(2)}</td>
                    <td className="px-2 py-2 text-right text-terminal-accent">{g.fair_prob.toFixed(2)}</td>
                    <td className="px-2 py-2 text-right">
                      <span className={g.edge_cents > 0 ? "text-green-400" : "text-red-400"}>
                        {g.edge_cents > 0 ? "+" : ""}{g.edge_cents}c
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        g.signal === "BUY_YES" ? "bg-green-600/20 text-green-400" :
                        g.signal === "BUY_NO" ? "bg-red-600/20 text-red-400" :
                        "bg-gray-800 text-gray-400"
                      }`}>
                        {g.signal}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Markets table */}
        <div className="terminal-window">
          <div className="terminal-header">
            <span className="flex items-center gap-2">
              <Activity size={12} />
              ACTIVE MARKETS
            </span>
            <span className="text-terminal-accent">{markets.length || 3} markets</span>
          </div>
          <div className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-terminal-border text-gray-500 text-xs">
                  <th className="text-left px-3 py-2">MARKET</th>
                  <th className="text-right px-3 py-2">PM PRICE</th>
                  <th className="text-right px-3 py-2">EDGE</th>
                  <th className="text-right px-3 py-2">SIGNAL</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-terminal-border/50 hover:bg-white/5">
                  <td className="px-3 py-2 text-white">BTC-5MIN</td>
                  <td className="px-3 py-2 text-right">0.54</td>
                  <td className="px-3 py-2 text-right text-terminal-accent">+24c</td>
                  <td className="px-3 py-2 text-right">
                    <span className="text-xs px-2 py-0.5 rounded bg-green-600/20 text-green-400">STRONG_BUY</span>
                  </td>
                </tr>
                <tr className="border-b border-terminal-border/50 hover:bg-white/5">
                  <td className="px-3 py-2 text-white">ETH-5MIN</td>
                  <td className="px-3 py-2 text-right">0.48</td>
                  <td className="px-3 py-2 text-right text-terminal-accent">+12c</td>
                  <td className="px-3 py-2 text-right">
                    <span className="text-xs px-2 py-0.5 rounded bg-terminal-accent/20 text-terminal-accent">BUY</span>
                  </td>
                </tr>
                <tr className="border-b border-terminal-border/50 hover:bg-white/5">
                  <td className="px-3 py-2 text-white">BTC-15MIN</td>
                  <td className="px-3 py-2 text-right">0.62</td>
                  <td className="px-3 py-2 text-right text-gray-500">+8c</td>
                  <td className="px-3 py-2 text-right">
                    <span className="text-xs px-2 py-0.5 rounded bg-gray-800 text-gray-400">HOLD</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* System Log */}
        <div className="terminal-window flex flex-col">
          <div className="terminal-header">
            <span className="flex items-center gap-2">
              <Clock size={12} />
              SYSTEM LOG
            </span>
            <span className="text-gray-500">LIVE</span>
          </div>
          <div className="flex-1 p-3 overflow-auto text-xs space-y-1 max-h-64">
            {logLines.map((line, i) => (
              <div key={i} className="text-gray-400 font-mono">
                <span className="text-terminal-accent">&gt;</span> {line}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Positions */}
      <div className="terminal-window">
        <div className="terminal-header">
          <span className="flex items-center gap-2">
            <TrendingUp size={12} />
            OPEN POSITIONS
          </span>
          <Link to="/portfolio" className="text-terminal-accent text-xs hover:underline">
            View full portfolio &rarr;
          </Link>
        </div>
        <div className="p-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-terminal-border text-gray-500 text-xs">
                <th className="text-left px-2 py-2">MARKET</th>
                <th className="text-left px-2 py-2">SIDE</th>
                <th className="text-right px-2 py-2">ENTRY</th>
                <th className="text-right px-2 py-2">SHARES</th>
                <th className="text-right px-2 py-2">VALUE</th>
                <th className="text-right px-2 py-2">STRATEGY</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-terminal-border/50 hover:bg-white/5">
                <td className="px-2 py-2">BTC-5MIN-UP</td>
                <td className="px-2 py-2 text-green-400">YES</td>
                <td className="px-2 py-2 text-right">0.54</td>
                <td className="px-2 py-2 text-right">185</td>
                <td className="px-2 py-2 text-right">$500.00</td>
                <td className="px-2 py-2 text-right text-terminal-accent">latency_arb</td>
              </tr>
              <tr className="border-b border-terminal-border/50 hover:bg-white/5">
                <td className="px-2 py-2">ETH-5MIN-DOWN</td>
                <td className="px-2 py-2 text-red-400">NO</td>
                <td className="px-2 py-2 text-right">0.48</td>
                <td className="px-2 py-2 text-right">208</td>
                <td className="px-2 py-2 text-right">$400.00</td>
                <td className="px-2 py-2 text-right text-terminal-accent">rare_event</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
